"use client";

import { type RefObject, useEffect, useId, useRef, useState } from "react";
import { cn } from "../../../lib";
import {
	buildMediaPlayerWaveformHeights,
	resolveWaveformPlayedBarCount,
} from "../../media-player/media-player-waveform-bars";
import {
	resolveRevealedBarRect,
	resolveWaveformBarRect,
} from "../audio-waveform-bar-geometry";
import { useMediaElementWaveform } from "../use-media-element-waveform";
import type { WaveformLevelTimeline } from "../waveform-level-timeline";
import {
	DEFAULT_BAR_STRETCH_BOUNCE_MS,
	DEFAULT_BUFFER_REVEAL_WIPE_MS,
	resolveBarStartPercent,
	resolveBarStretchWavefrontMs,
	resolvePerBarLinearStretchProgress,
	resolvePerBarStretchProgress,
} from "../waveform-bar-visual";

const WAVE_HEIGHT_PX = 22;
const BAR_SLOT_WIDTH = 10;
const PREPARE_DIM_OPACITY = 0.22;
const BUFFER_BOUNCE_CLIP_PAD_Y = WAVE_HEIGHT_PX * 0.65;

interface InteractiveAudioSegmentWaveformColumnProps {
	barCount: number;
	waveformSeed: string;
	fillPercent: number;
	volumePercent?: number;
	displayRevealPercent?: number;
	showBufferReveal?: boolean;
	bufferRevealSessionKey?: number;
	bufferRevealSessionStartMs?: number;
	bufferRevealWipeDurationMs?: number;
	isLocked?: boolean;
	audioRef?: RefObject<HTMLAudioElement | null>;
	waveformTimeline?: WaveformLevelTimeline | null;
	/** Active playing segment — sample timeline / analyser instead of static seed. */
	isLive?: boolean;
	isPreparingPlayback?: boolean;
	className?: string;
}

export function InteractiveAudioSegmentWaveformColumn({
	barCount,
	waveformSeed,
	fillPercent,
	volumePercent = 100,
	displayRevealPercent = 0,
	showBufferReveal = false,
	bufferRevealSessionKey = 0,
	bufferRevealSessionStartMs = 0,
	bufferRevealWipeDurationMs = DEFAULT_BUFFER_REVEAL_WIPE_MS,
	isLocked = false,
	audioRef,
	waveformTimeline = null,
	isLive = false,
	isPreparingPlayback = false,
	className,
}: InteractiveAudioSegmentWaveformColumnProps) {
	const idSuffix = useId().replace(/:/g, "");
	const fallbackAudioRef = useRef<HTMLAudioElement | null>(null);
	const transportRef = audioRef ?? fallbackAudioRef;
	const playbackTimeSeconds =
		waveformTimeline != null && waveformTimeline.durationSeconds > 0
			? (Math.max(0, Math.min(100, fillPercent)) / 100) *
				waveformTimeline.durationSeconds
			: null;
	const { barHeights: liveHeights } = useMediaElementWaveform({
		audioRef: transportRef,
		transportAudioRef: transportRef,
		waveformTimeline,
		playbackTimeSeconds,
		enabled: isLive && !isPreparingPlayback && !showBufferReveal,
		waveformSeed,
		barCount,
		allowSharedTransportAnalyser: false,
	});
	const staticHeights = buildMediaPlayerWaveformHeights(
		waveformSeed,
		barCount,
	);
	const heights =
		isLive && !isPreparingPlayback && !showBufferReveal
			? liveHeights
			: staticHeights;
	const volumeScale = Math.max(0, Math.min(100, volumePercent)) / 100;
	const playedCount = resolveWaveformPlayedBarCount(fillPercent, barCount);
	const viewWidth = barCount * BAR_SLOT_WIDTH;
	const playheadClipWidth = (fillPercent / 100) * viewWidth;
	const bufferClipWidth = (displayRevealPercent / 100) * viewWidth;
	const barOptions = {
		waveHeightPx: WAVE_HEIGHT_PX,
		barSlotWidth: BAR_SLOT_WIDTH,
	};
	const [reduceMotion, setReduceMotion] = useState(false);
	const [stretchNowMs, setStretchNowMs] = useState(0);
	const bufferRevealSessionStartMsRef = useRef(bufferRevealSessionStartMs);
	const lastBufferSessionKeyRef = useRef(bufferRevealSessionKey);

	useEffect(() => {
		const media = window.matchMedia("(prefers-reduced-motion: reduce)");
		const update = () => setReduceMotion(media.matches);
		update();
		media.addEventListener("change", update);
		return () => media.removeEventListener("change", update);
	}, []);

	useEffect(() => {
		if (bufferRevealSessionKey !== lastBufferSessionKeyRef.current) {
			bufferRevealSessionStartMsRef.current =
				bufferRevealSessionStartMs > 0
					? bufferRevealSessionStartMs
					: performance.now();
			lastBufferSessionKeyRef.current = bufferRevealSessionKey;
		}
	}, [bufferRevealSessionKey, bufferRevealSessionStartMs]);

	useEffect(() => {
		if (bufferRevealSessionStartMs > 0) {
			bufferRevealSessionStartMsRef.current = bufferRevealSessionStartMs;
		}
	}, [bufferRevealSessionStartMs]);

	useEffect(() => {
		if (!showBufferReveal) {
			return;
		}

		let frame = 0;
		const tick = () => {
			setStretchNowMs(performance.now());
			frame = requestAnimationFrame(tick);
		};

		frame = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(frame);
	}, [showBufferReveal, bufferRevealSessionKey]);

	const renderBars = (emphasizePlayed = false) =>
		heights.map((height, index) => {
			const rect = resolveWaveformBarRect(
				height * volumeScale,
				index,
				barOptions,
			);
			const isPlayed = index < playedCount;
			const opacity = emphasizePlayed ? (isPlayed ? 1 : 0.22) : undefined;

			return (
				<rect
					key={`${emphasizePlayed ? "bright" : "dim"}-${index}`}
					x={rect.x}
					y={rect.y}
					width={rect.width}
					height={rect.height}
					rx={2}
					className="fill-white dark:fill-white"
					opacity={opacity}
				/>
			);
		});

	const renderPrepareDotBars = () =>
		heights.map((height, index) => {
			const barStart = resolveBarStartPercent(index, barCount);
			if (displayRevealPercent >= barStart) {
				return null;
			}

			const rect = resolveRevealedBarRect(
				height * volumeScale,
				index,
				0,
				barOptions,
			);

			return (
				<rect
					key={`prepare-dot-${index}`}
					x={rect.x}
					y={rect.y}
					width={rect.width}
					height={rect.height}
					rx={Math.min(rect.width, rect.height) / 2}
					ry={Math.min(rect.width, rect.height) / 2}
					className="fill-white dark:fill-white"
					style={{ opacity: PREPARE_DIM_OPACITY }}
				/>
			);
		});

	const renderPrepareStretchBars = () => {
		const nowMs = stretchNowMs || performance.now();
		const sessionStartMs =
			bufferRevealSessionStartMsRef.current > 0
				? bufferRevealSessionStartMsRef.current
				: nowMs;

		return heights.map((height, index) => {
			const barStart = resolveBarStartPercent(index, barCount);
			if (displayRevealPercent < barStart) {
				return null;
			}

			const wavefrontMs = resolveBarStretchWavefrontMs(
				index,
				barCount,
				bufferRevealWipeDurationMs,
			);
			const elapsedMs = Math.max(0, nowMs - sessionStartMs - wavefrontMs);
			const linearStretch = resolvePerBarLinearStretchProgress(elapsedMs);
			const stretchProgress = resolvePerBarStretchProgress(
				elapsedMs,
				DEFAULT_BAR_STRETCH_BOUNCE_MS,
				!reduceMotion,
			);
			const rect = resolveRevealedBarRect(
				height * volumeScale,
				index,
				stretchProgress,
				barOptions,
			);
			const cornerRadius =
				linearStretch < 1 ? Math.min(rect.width, rect.height) / 2 : 2;

			return (
				<rect
					key={`prepare-stretch-${index}`}
					x={rect.x}
					y={rect.y}
					width={rect.width}
					height={rect.height}
					rx={cornerRadius}
					ry={cornerRadius}
					className="fill-white dark:fill-white"
					style={{
						opacity: PREPARE_DIM_OPACITY * linearStretch,
					}}
				/>
			);
		});
	};

	return (
		<div
			className={cn(
				"relative flex h-full min-w-0 flex-1 items-center overflow-hidden",
				isLocked && "opacity-40",
				className,
			)}
		>
			<svg
				className="h-[22px] w-full overflow-visible"
				viewBox={`0 0 ${viewWidth} ${WAVE_HEIGHT_PX}`}
				preserveAspectRatio="none"
				aria-hidden="true"
			>
				{showBufferReveal ? (
					<>
						<defs>
							<clipPath id={`buffer-${idSuffix}`}>
								<rect
									x={0}
									y={-BUFFER_BOUNCE_CLIP_PAD_Y}
									width={bufferClipWidth}
									height={
										WAVE_HEIGHT_PX +
										BUFFER_BOUNCE_CLIP_PAD_Y * 2
									}
								/>
							</clipPath>
						</defs>
						<g>{renderPrepareDotBars()}</g>
						<g clipPath={`url(#buffer-${idSuffix})`}>
							{renderPrepareStretchBars()}
						</g>
					</>
				) : (
					<>
						<defs>
							<clipPath id={`playhead-${idSuffix}`}>
								<rect
									x={0}
									y={0}
									width={playheadClipWidth}
									height={WAVE_HEIGHT_PX}
								/>
							</clipPath>
						</defs>
						<g opacity={0.22}>{renderBars()}</g>
						<g clipPath={`url(#playhead-${idSuffix})`}>
							{renderBars(true)}
						</g>
					</>
				)}
			</svg>
		</div>
	);
}
